// filepath: src/components/Home.jsx
import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Container,
  Box,
  Typography,
  Grid,
  Button,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion"; 
import InsightsIcon from '@mui/icons-material/Insights';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';

// --- AI Kernel Imports ---
import { useKernel } from "./Shortener/hooks/AIKernel";
import { useFrustrationSensor } from "./Shortener/hooks/useFrustrationSensor";
import { useBehaviorSensor } from "./Shortener/hooks/useBehaviorSensor"; // 🧠 Now Actively Used!

// --- ADVANCED ANIMATION VARIANTS ---
const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.2 } }, 
};

const textVariant = {
  hidden: { y: "100%", opacity: 0 },
  visible: { y: "0%", opacity: 1, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

const fadeInUp = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.85 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: "easeInOut" } },
};

const featureIcons = {
    optimization: RocketLaunchIcon,
    monitoring: MonitorHeartIcon,
    analytics: InsightsIcon,
};

const MotionPaper = motion(Paper);
const MotionButton = motion(Button);
const MotionTypography = motion(Typography);
const MotionBox = motion(Box);

const Home = () => {
  // --- AI Engine Integration ---
  const { agentState, reportBehavior } = useKernel();
  
  // 🧠 Sensor 1: Tracks global rage-clicks
  useFrustrationSensor(); 
  
  // 🧠 Sensor 2: Tracks Dwell Time (How long they read specific sections)
  const heroSensorRef = useBehaviorSensor('reading_focus_hero', 1);
  const featuresSensorRef = useBehaviorSensor('reading_focus_features', 1.5); // Higher weight for reading features
  const statsSensorRef = useBehaviorSensor('reading_focus_stats', 0.8);

  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const title1Words = t("home.hero.title1").split(" ");
  const title2Words = t("home.hero.title2").split(" ");

  const bgAnimationProps = {
    repeat: Infinity,
    duration: isMobile ? 40 : 25,
    ease: "linear",
  };
  
  const FeatureIcon = (feature) => {
    const IconComponent = featureIcons[feature] || RocketLaunchIcon;
    return <IconComponent sx={{ fontSize: 32 }} />;
  };

  // 🧠 AI Actuator: If user is frustrated, disable animations
  const shouldSimplify = agentState.ui.simplifyAnimations;

  const handleHeroCTA = (type) => {
      reportBehavior(`intent_hero_${type}`, 5, 'interest');
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        position: "relative",
        overflowX: "hidden",
      }}
      className={agentState.theme} // Apply AI Theme
    >
      {/* Background Animated Shapes */}
      {!shouldSimplify && (
          <>
            <motion.div
              animate={{ x: ["-100%", "100%"], opacity: [0, 0.3, 0.3, 0] }}
              transition={{ ...bgAnimationProps, times: [0, 0.2, 0.8, 1] }}
              style={{
                position: "absolute",
                top: "20%",
                width: isMobile ? "100px" : "200px",
                height: isMobile ? "100px" : "200px",
                background: "linear-gradient(135deg, #d7b879, #f0d79f)",
                borderRadius: "50%",
                filter: "blur(70px)",
                zIndex: 0,
              }}
            />
            <motion.div
              animate={{ y: ["100%", "-100%"], opacity: [0, 0.25, 0.25, 0] }}
              transition={{ ...bgAnimationProps, duration: isMobile ? 50 : 35, times: [0, 0.2, 0.8, 1] }}
              style={{
                position: "absolute",
                right: "10%",
                width: isMobile ? "150px" : "300px",
                height: isMobile ? "150px" : "300px",
                background: "linear-gradient(135deg, #a98b5d, #dfc892)",
                borderRadius: "50%",
                filter: "blur(90px)",
                zIndex: 0,
              }}
            />
          </>
      )}

      {/* Hero Section - Attached Dwell Time Sensor */}
      <Container 
        ref={heroSensorRef} 
        maxWidth="lg" 
        sx={{ py: { xs: 8, md: 14 }, position: "relative", zIndex: 1 }}
      >
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            <motion.div 
                variants={staggerContainer} 
                initial={shouldSimplify ? "visible" : "hidden"} 
                animate="visible"
            >
              <Typography 
                variant={isMobile ? "h4" : "h3"}
                component="h1" 
                fontWeight="bold" 
                gutterBottom
                sx={{ overflow: 'hidden' }}
              >
                <Box component="span" sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' }, flexWrap: 'wrap' }}>
                    {title1Words.map((word, index) => (
                        <MotionBox component="span" variants={textVariant} key={index} sx={{ mr: 1 }}>
                            {word}
                        </MotionBox>
                    ))}
                </Box>
                <Box component="span" sx={{ color: theme.palette.primary.main, display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' }, flexWrap: 'wrap' }}>
                    {title2Words.map((word, index) => (
                        <MotionBox component="span" variants={textVariant} key={index} sx={{ mr: 1 }}>
                            {word}
                        </MotionBox>
                    ))}
                </Box>
              </Typography>
            </motion.div>
            
            <motion.div 
                variants={fadeInUp} 
                initial={shouldSimplify ? "visible" : "hidden"} 
                animate="visible" 
                transition={{ delay: shouldSimplify ? 0 : 0.8 }}
            >
              <Typography 
                variant={isMobile ? "body1" : "h6"}
                color="text.secondary" 
                sx={{ mb: 4 }}
              >
                {t("home.hero.description")}
              </Typography>
            </motion.div>
            
            <Box 
                sx={{ 
                    display: "flex", 
                    gap: 2, 
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'center', md: 'flex-start' },
                    '& > *': { width: { xs: '100%', sm: 'auto' } }
                }}
            >
                <MotionButton
                    component={Link}
                    to="/features"
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={() => handleHeroCTA('get_started')}
                    whileHover={!shouldSimplify ? { scale: 1.05, boxShadow: "0 8px 20px rgba(215, 184, 121, 0.28)" } : {}}
                    whileTap={!shouldSimplify ? { scale: 0.95 } : {}}
                    className={agentState.ui.highlightCTAs ? 'cta-highlight-glow' : ''}
                >
                    {t("home.cta.getStarted")}
                </MotionButton>
                <MotionButton
                    component={Link}
                    to="/contact"
                    variant="outlined"
                    color="primary"
                    size="large"
                    onClick={() => handleHeroCTA('contact_sales')}
                    whileHover={!shouldSimplify ? { scale: 1.05, backgroundColor: theme.palette.primary.light, color: 'white' } : {}}
                    whileTap={!shouldSimplify ? { scale: 0.95 } : {}}
                >
                    {t("home.cta.contactSales")}
                </MotionButton>
            </Box>
          </Grid>

          <Grid item xs={12} md={6} sx={{ order: { xs: -1, md: 0 } }}>
            <motion.div 
                variants={scaleIn} 
                initial={shouldSimplify ? "visible" : "hidden"} 
                animate="visible"
            >
              <Box
                sx={{
                  width: "100%",
                  height: { xs: 200, sm: 280, md: 350 },
                  borderRadius: 4,
                  background: "linear-gradient(135deg, #8f7040, #d7b879, #f0d79f)",
                  boxShadow: 8,
                }}
              />
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* Features Section - Attached Dwell Time Sensor */}
      <Box 
        ref={featuresSensorRef} 
        sx={{ py: { xs: 8, md: 14 }, bgcolor: "background.paper" }}
      >
        <Container maxWidth="lg">
            <motion.div 
                variants={fadeInUp} 
                initial={shouldSimplify ? "visible" : "hidden"} 
                whileInView="visible" 
                viewport={{ once: true, amount: 0.5 }} 
                sx={{ textAlign: 'center', mb: 6 }}
            >
                <Typography variant="h5" color="primary.main" fontWeight="medium">
                    {t("home.features.subtitle")}
                </Typography>
                <Typography variant="h3" fontWeight="bold">
                    {t("home.features.mainTitle")}
                </Typography>
            </motion.div>
          
          <motion.div 
            variants={staggerContainer} 
            initial={shouldSimplify ? "visible" : "hidden"} 
            whileInView="visible" 
            viewport={{ once: true, amount: 0.2 }}
          >
            <Grid container spacing={4}>
              {["optimization", "monitoring", "analytics"].map((feature, idx) => (
                <Grid item xs={12} sm={6} md={4} key={idx}>
                  <motion.div variants={fadeInUp}>
                    <MotionPaper 
                      elevation={3}
                      onClick={() => reportBehavior(`feature_interest_${feature}`, 2, 'interest')}
                      whileHover={!shouldSimplify ? { 
                          scale: 1.03, 
                          boxShadow: "0 10px 20px rgba(0, 0, 0, 0.15)",
                          translateY: -5 
                      } : {}}
                      whileTap={!shouldSimplify ? { scale: 0.98 } : {}}
                      sx={{
                        p: { xs: 3, md: 4 },
                        borderRadius: 3,
                        minHeight: 200,
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: 'pointer',
                      }}
                    >
                      <Box sx={{ color: "primary.main", mb: 2 }}>
                        {FeatureIcon(feature)}
                      </Box>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {t(`home.features.${feature}.title`)}
                      </Typography>
                      <Typography color="text.secondary">
                        {t(`home.features.${feature}.description`)}
                      </Typography>
                    </MotionPaper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* CTA Section (Bottom) */}
      <MotionBox 
        variants={fadeInUp} 
        initial={shouldSimplify ? "visible" : "hidden"} 
        whileInView="visible" 
        viewport={{ once: true }} 
        sx={{ py: { xs: 8, md: 14 }, bgcolor: "primary.main", color: "white" }}
      >
        <Container
          maxWidth="lg"
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            textAlign: { xs: 'center', md: 'left' }
          }}
        >
            <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" gutterBottom>
              <span>{t("home.ctaBottom.title1")}</span>
              <span style={{ color: "#c7d2fe", display: "block" }}>
                {t("home.ctaBottom.title2")}
              </span>
            </Typography>
            <MotionButton
                component={Link}
                to="/dashboard"
                variant="contained"
                color="secondary"
                size="large"
                onClick={() => reportBehavior('intent_dashboard_bottom', 5, 'interest')}
                whileHover={!shouldSimplify ? { scale: 1.05, boxShadow: "0 8px 15px rgba(255, 255, 255, 0.3)" } : {}}
                whileTap={!shouldSimplify ? { scale: 0.95 } : {}}
                className={agentState.ui.highlightCTAs ? 'cta-highlight-glow' : ''}
                sx={{ 
                    mt: { xs: 4, md: 0 },
                    width: { xs: '100%', sm: 'auto' }
                }}
            >
                {t("home.ctaBottom.button")}
            </MotionButton>
        </Container>
      </MotionBox>

      {/* Stats Section - Attached Dwell Time Sensor */}
      <Container ref={statsSensorRef} maxWidth="lg" sx={{ py: { xs: 8, md: 14 } }}>
        <Grid container spacing={4}>
          <motion.div 
            variants={staggerContainer} 
            initial={shouldSimplify ? "visible" : "hidden"} 
            whileInView="visible" 
            viewport={{ once: true, amount: 0.5 }}
            style={{ width: '100%' }}
          >
            <Grid container spacing={4}>
                {[
                    { value: "%", label: t("home.stats.containers") },
                    { value: "100+", label: t("home.stats.images") },
                    { value: "95%", label: t("home.stats.alerts") },
                    { value: "24/7", label: t("home.stats.uptime") },
                ].map((stat, idx) => (
                    <Grid item xs={6} md={3} textAlign="center" key={idx}>
                    <motion.div variants={fadeInUp}>
                        <Typography variant={isMobile ? "h4" : "h3"} fontWeight="bold" color="primary.main">
                        {stat.value}
                        </Typography>
                        <Typography color="text.secondary" variant={isMobile ? "body2" : "body1"}>
                        {stat.label}
                        </Typography>
                    </motion.div>
                    </Grid>
                ))}
            </Grid>
          </motion.div>
        </Grid>
      </Container>
    </Box>
  );
};

export default Home;